import type { ReadPoliceReport } from '../api/generated/policeReportsService';
import { TreatmentRecordTypes } from '../api/generated/treatmentRecordRequestsService';
import { MessageAttachment } from '../frontService';
import type { Blob, BlobCreateRequest, BlobUpdateRequest } from './blob-types';
import { CollectionDetails, CollectionNode } from './collection-types';
import type { CoverageStub } from './coverage-types';
import { DataRequirement } from './data-requirements-types';
import type { DeliverableStub } from './deliverables-types';
import type { Form } from './form-types';
import type { MatterClient } from './matter-client-types';
import type {
  ClientEmploymentNote,
  DamageNote,
  EmergencyContactNote,
  IncidentNote,
  InsuranceNote,
  InvolvedPersonNote,
  LawEnforcementNote,
  PriorLawsuitNote,
  PriorMedicalNote,
} from './matter-notes-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
  SLAClockStartEvent,
  SLAPolicy,
  TaskExternalCMSUpdateStatus,
  TaskPriorityLabel,
} from './task-types';
import type {
  InferredInjuryType,
  MedicalProvider,
  MedicalProviderStub,
} from './treatment-types';
import type { User } from './user-types';

export enum CrashDocsStatus {
  REPORT_FOUND = 'REPORT_FOUND',
  REPORT_NOT_FOUND = 'REPORT_NOT_FOUND',
  ERROR = 'ERROR',
}

export interface CrashDocsLookupResponse {
  link?: string;
  status: CrashDocsStatus;
  lookup_id?: string;
}

export interface CrashDocsLookupStatusResponse {
  lookup_id: string;
  status: string; // 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  message?: string;
  report_url?: string;
  created_at: string;
  updated_at: string;
  search_details: {
    state: string;
    jurisdiction: string;
    last_name: string;
    incident_date: string;
    report_id: string | null;
  };
  task_id: string;
  estimated_seconds_remaining?: number;
}

interface TaskAssignee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface TaskTypeAction {
  id: string;
  name: string;
  order: number;
  template: string;
}

interface TaskSearchResults {
  count: number;
  next: string | null;
  previous: string | null;
  results: Task[];
}

interface PhoneCallAction {
  template: string;
  phone_number: string;
  provider: string;
  interrupt: boolean;
  bland_call_id?: string;
  call_status?: string;
}

interface TaskActionResult {
  url: string;
  status: string;
}

interface TaskStatus {
  id: string;
  order: number;
  name: string;
  canonical_status: CanonicalTaskStatus;
  description: string;
}

interface TaskType {
  id: string;
  order: number;
  name: string;
  canonical_group?: CanonicalTaskGroup;
  group: string;
  description: string;
  canonical_type: CanonicalTaskType;
}

interface TaskGroup {
  id: string;
  order: number;
  name: string;
  description: string;
  canonical_group: CanonicalTaskGroup;
}

export interface TreatmentRecordRequest {
  id: string;
  provider: string;
  provided_records: TreatmentRecordTypes[];
  /** TRR status from TaskListSerializer (e.g. "pending", "request_submitted") */
  status?: string;
  /** Provider ID from TaskListSerializer */
  provider_id?: string;
  /** Provider name from TaskListSerializer (via select_related provider) */
  provider_name?: string | null;
  /** Start of the date-of-service range (ISO date string, e.g. "2025-01-15") */
  service_start_date?: string | null;
  /** End of the date-of-service range (date string or "Present") */
  service_end_date?: string | null;
}

// Task metadata structure
interface TaskMetadata {
  tags?: string[];
  [key: string]: unknown;
}

// Types for Task and TaskUpdate
interface Task {
  id: string;
  matter: string;
  name: string;
  description?: string;
  start_date?: string;
  due_date?: string;
  completed_at?: string;
  // This parameter is confusingly named. We're using it to mark tasks as "urgent"
  // and sort them to the top of the list.
  order?: number;
  created_at: string;
  modified_at: string;
  type: TaskType;
  group: TaskGroup;
  status: TaskStatus;
  attachments?: Blob[];
  firm_name: string;
  firm: string;
  firm_primary_state?: string;
  matter_name: string;
  matter_status?: string | null;
  // Companion case fields (mirrors MatterListItem pattern)
  companion_count?: number;
  is_companion_primary?: boolean;
  incident_type_display?: string | null;
  assignee: TaskAssignee | null;
  medical_note?: MedicalProviderStub;
  medical_note_id?: string;
  coverage?: CoverageStub;
  coverage_id?: string;
  /** Carrier name extracted from coverage JSON (list view only, replaces full coverage object) */
  coverage_carrier_name?: string | null;
  deliverables?: DeliverableStub[];
  data_requirements?: DataRequirement[];
  updates?: SimpleTaskUpdate[];
  // Last update metadata (from annotations, prevents N+1 queries)
  updates_count?: number;
  latest_update_at?: string | null;
  latest_update_user_name?: string | null;
  latest_update_actor_type?: string | null;
  latest_update_actor_label?: string | null;
  latest_update_text?: string | null;
  police_report?: ReadPoliceReport;
  police_report_id?: string;
  police_departments?: Array<{ id: string; name: string }>;
  treatment_record_request?: TreatmentRecordRequest;
  metadata?: TaskMetadata;
  has_rainmaker?: boolean;
  external_cms_update_status: TaskExternalCMSUpdateStatus;
  external_cms_update_reason?: string | null;
  /** Computed priority label from backend (ensures 1:1 match with sort order) */
  priority_label?: TaskPriorityLabel;
  // SLA fields
  sla_last_update?: string | null;
  sla_clock_start?: string | null;
  sla_clock_start_event?: SLAClockStartEvent | null;
  sla_resolution_target?: string | null;
  sla_resolution_policy?: SLAPolicy | null;
  sla_first_action_target?: string | null;
  sla_first_action_policy?: SLAPolicy | null;
  // Task source fields (where the task originated from)
  source?: 'finch_system' | 'finch_user' | 'finch_custom_task' | 'external_cms';
  source_display?: string;
  /** Name of the user who created the task (only set for finch_user source) */
  created_by_name?: string | null;
  /** Template type for distinguishing task variants within the same TaskType */
  template_type?: string | null;
  /** Finch Academy playbook URL for this task's canonical type (null if no playbook) */
  playbook_url?: string | null;
  crashing_out_agent_status?: 'working' | 'needs_review' | 'not_running' | null;
  crashing_out_latest_tool?: string | null;
  crashing_out_latest_tool_status?: string | null;
}

interface SimpleTaskUpdate {
  id: string;
  user?: {
    id: string;
    display_name?: string;
    email?: string;
  };
  created_at: string;
}

/**
 * User object shape when TaskUpdate.user is populated as an object.
 * Can also be a string (user ID) or null in some cases.
 */
export interface TaskUpdateUser {
  id: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  email?: string;
}

interface TaskUpdate {
  id: string;
  task: string;
  update: string;
  update_type?: 'comment' | 'activity';
  visibility: string;
  agent: string | null;
  user: TaskUpdateUser | string | null;
  created_at: string;
  modified_at: string;
  attachments: Blob[];
  /** Links this update to domain events that occurred as part of the same action */
  correlation_id?: string | null;
  /** Optional reason for the change (e.g., "Client requested", "Error correction") */
  change_reason?: string | null;
}

interface UpdateTaskPayload {
  name?: string | null;
  description?: string | null;
  due_date?: string | null;
  start_date?: string | null;
  completed_at?: string | null;
  order?: number | null;
  type_id?: string | null;
  group_id?: string | null;
  status_id?: string | null;
  attachments?: string[] | null;
  assignee_id?: string | null;
  medical_note_id?: string | null;
  coverage_id?: string | null;
  police_report_id?: string | null;
  // SLA fields
  sla_last_update?: string | null;
  sla_clock_start?: string | null;
  sla_clock_start_event?: SLAClockStartEvent | null;
  sla_resolution_target?: string | null;
  sla_resolution_policy?: SLAPolicy | null;
  sla_first_action_target?: string | null;
  sla_first_action_policy?: SLAPolicy | null;
  // Field change tracking (optional - creates a linked TaskUpdate comment)
  change_reason?: string;
  // Allow TRR task completion without attachments (e.g. firm already has records)
  skip_attachment_validation?: boolean;
}

interface UpdateTaskExternalCMSStatusPayload {
  external_cms_update_status: TaskExternalCMSUpdateStatus;
  external_cms_update_reason?: string | null;
}

export enum TaskUpdateVisibility {
  PRIVATE = 'Private',
  PUBLIC = 'Public',
}

interface CreateTaskUpdatePayload {
  update: string;
  visibility: TaskUpdateVisibility;
  attachments?: string[] | null;
  alert_mentioned?: boolean;
}

interface SharedMatter {
  expires_at: string;
  status: string;
}

interface ShareMatterLink {
  token: string;
  matter_id: string;
  email: string;
  created_at: string;
  last_accessed_at: string | null;
  expires_at: string;
}

interface Engagement {
  id: string;
  status: string;
  letter_path: string;
  matter: string;
  client: string;
}

interface Conversation {
  id: string;
  format: string;
  name: string;
  transcript_path: string;
  audio_path: string;
  duration: number;
  firm_user: string;
  start_timestamp: string;
  end_timestamp: string;
  matter: string;
  client: string;
  transcript_artifact_id: string;
}

type MatterKeyConsiderationSeverity = 'Green' | 'Yellow' | 'Red' | 'Gray';

export const MatterKeyConsiderationSeverities: MatterKeyConsiderationSeverity[] =
  ['Green', 'Yellow', 'Red', 'Gray'];

export enum MatterKeyConsiderationCategory {
  // Mandatory Flags
  CLEAR_LIABILITY = 'Clear Liability',
  SIGNIFICANT_INJURIES = 'Significant Injuries',
  INSURANCE_COVERAGE = 'Insurance Coverage',

  // Yellow Flags
  POTENTIALLY_TOUGH_CLIENT = 'Potentially Tough Client',
  CLIENT_IS_A_MINOR = 'Client is a Minor',
  HAD_PREVIOUS_REPRESENTATION = 'Had Previous Representation',
  PRE_EXISTING_INJURIES = 'Pre-existing Injuries',
  MINIMAL_PROPERTY_DAMAGE = 'Minimal Property Damage',
  PRIOR_LEGAL_ISSUES = 'Prior Legal Issues',
  SUBSTANCE_USE = 'Substance Use',
  INCONSISTENT_STATEMENTS = 'Inconsistent Statements',
  INSUFFICIENT_EVIDENCE = 'Insufficient Evidence',

  // Red Flags
  CLIENT_CRIMINAL_ACTIVITY = 'Client Criminal Activity',
  EVIDENCE_CONTRADICTS_CLIENT = 'Evidence Contradicts Client',
  STATUTE_OF_LIMITATIONS = 'Statute of Limitations',
  MEDICAL_TREATMENT_DELAYED = 'Medical Treatment Delayed',
  PAST_MEDICAL_HISTORY = 'Past Medical History',
}

export const MandatoryFlags: MatterKeyConsiderationCategory[] = [
  MatterKeyConsiderationCategory.CLEAR_LIABILITY,
  MatterKeyConsiderationCategory.SIGNIFICANT_INJURIES,
  MatterKeyConsiderationCategory.INSURANCE_COVERAGE,
];

interface MatterKeyConsideration {
  id: string;
  category: MatterKeyConsiderationCategory;
  severity: MatterKeyConsiderationSeverity;
  content: string;
}

interface MatterSummary {
  id: string;
  category: 'Accident' | 'Damages';
  content: string;
}

interface ActionItem {
  id: string;
  content: string;
  matter: string;
}

export interface Firm {
  id: string;
  name: string;
  managing_paralegal?: User | null;
  full_name?: string | null;
  email?: string | null;
  sms_sender_number?: string | null;
}

export enum MatterType {
  MOTOR_VEHICLE_ACCIDENT = 'Motor Vehicle Accident',
  MEDICAL_MALPRACTICE = 'Medical Malpractice',
  MISCELLANEOUS = 'Miscellaneous',
  BREACH_OF_EMPLOYMENT_CONTRACT = 'Breach of Employment Contract',
  SELLER_NON_DISCLOSURE = 'Seller Non-Disclosure',
  PREMISES_LIABILITY = 'Premises Liability',
  BROKER_NEGLIGENCE = 'Broker Negligence',
  PROPERTY_DAMAGE = 'Property Damage',
  DOG_BITE = 'Dog Bite',
  WRONGFUL_DEATH = 'Wrongful Death',
  MOLD = 'Mold',
  UNKNOWN = 'Unknown',
}

export enum SimplifiedBillingCode {
  RECORDS_RETRIEVAL = 'records_retrieval',
  DEMAND_ONLY = 'demand_only',
  PROPERTY_DAMAGE = 'property_damage',
  IN_FLIGHT = 'in_flight',
  BACK_OFFICE = 'back_office',
  FULL_PRE_LIT = 'full_pre_lit',
  FULL_PRE_LIT_PROPERTY_DAMAGE = 'full_pre_lit_property_damage',
  CASE_SETUP_ONLY = 'case_setup_only',
  LITIGATION_ONLY = 'litigation_only',
}

interface BaseMatter {
  id: string;
  name: string;
  type: string;
  matter_type?: MatterType;
  status?: string;
  status_entered_at?: string | null;
  inferred_injury_type?: InferredInjuryType | null;
  inferred_injury_type_confidence?: number | null;
  inferred_injury_type_source?: 'AI' | 'MANUAL' | null;
  inferred_injury_type_updated_at?: string | null;
  inferred_injury_type_reasoning?: string | null;
  firm: Firm;
  intake_id?: string | null;
  retainer_signed?: boolean;
  client_phone_numbers?: string[];
  date_of_loss?: string;
  city?: string;
  state?: string;
  statute_of_limitations_date?: string;
  filed_date?: string | null;
  contact_client_directly?: boolean;
  preferred_language?: string;
  created_at?: string;
  modified_at?: string;
  external_cms_url?: string | null;
  external_drive_url?: string | null;
  cms_email?: string | null;
  case_id?: string | null;
  referral_source_description?: string;
  companion_root_id?: string | null;
  companion_count?: number;
  is_companion_primary?: boolean;
  coverage_creation_locked?: boolean;
  representing_attorney?: string | { id: string; name?: string } | null;
  representing_attorney_name?: string | null;
  last_contacted_at?: string | null;
  case_manager?: { id: string; display_name: string } | null;
  simplified_billing_code?: SimplifiedBillingCode | null;
}

interface RelatedMatter {
  id: string;
  name: string;
  status: string;
  created_at: string;
  shared_phone: string;
}

interface BaseDetailedMatter extends BaseMatter {
  creator?: User;
  engagements: Engagement[];
  clients: MatterClient[];
  conversations: Conversation[];
  key_considerations: MatterKeyConsideration[];
  summaries: MatterSummary[];
  action_items: ActionItem[];
  form?: Form;
  intake_note?: string;
  incident_note?: IncidentNote;
  emergency_contact_note?: EmergencyContactNote;
  prior_medical_notes?: PriorMedicalNote[];
  prior_lawsuit_notes?: PriorLawsuitNote[];
  client_employment_note?: ClientEmploymentNote;
  involved_person_notes?: InvolvedPersonNote[];
  law_enforcement_notes?: LawEnforcementNote[];
  medical_notes?: MedicalProvider[];
  insurance_notes?: InsuranceNote[];
  damage_notes: DamageNote[];
  details_type?: string;
  details?: any;
  modified_at?: string;
  hipaa_authorization_template_id?: string;
  related_matters?: RelatedMatter[];
  external_cms_url?: string | null;
  external_drive_url?: string | null;
  /** Task configuration for controlling task visibility during initial review */
  task_config?: {
    gate_tasks_during_initial_review?: boolean;
  };
}

type DetailedMatter = BaseDetailedMatter;

type MatterUpdate = Partial<
  Pick<
    BaseMatter,
    | 'name'
    | 'matter_type'
    | 'status'
    | 'firm'
    | 'city'
    | 'state'
    | 'contact_client_directly'
    | 'external_cms_url'
    | 'external_drive_url'
    | 'cms_email'
    | 'case_id'
    | 'retainer_signed'
    | 'referral_source_description'
    | 'simplified_billing_code'
  >
> & {
  intake_note?: string;
  representing_attorney?: string | null;
  hipaa_authorization_template_id?: string;
  date_of_loss?: string;
  statute_of_limitations_date?: string;
  filed_date?: string | null;
  coverage_creation_locked?: boolean;
  /** Manual override for last contact date (takes precedence over auto-computed value) */
  last_contacted_at_override?: string | null;
};

export enum MatterStatus {
  INTAKE = 'Intake',
  CASE_INGESTION = 'Case Ingestion',
  INITIAL_REVIEW = 'Initial Review',
  CASE_SETUP = 'Case Setup',
  TREATMENT = 'Treatment',
  TREATMENT_COMPLETE = 'Treatment Complete',
  READY_FOR_DEMAND = 'Ready for Demand',
  DEMAND_PREPARATION = 'Demand Preparation',
  DEMAND_LETTER = 'Demand Letter',
  LITIGATION = 'Litigation',
  COMPLETE = 'Complete',
  HIDDEN = 'Hidden',
  CLIENT_DROPPED = 'Client Dropped',
  TO_BE_DROPPED = 'To Be Dropped',
  RETURNED_TO_FIRM = 'Returned to Firm',
  INGESTED_IN_ERROR = 'Ingested in Error',
}

export interface CreateMatterRequest {
  firm: string;
  name: string;
  status?: MatterStatus;
  type?: string;
  matter_type?: MatterType;
  date_of_loss?: string;
  city?: string;
  state?: string;
  statute_of_limitations_date?: string;
  contact_client_directly?: boolean;
  external_cms_url?: string | null;
  external_drive_url?: string | null;
  cms_email?: string | null;
  case_id?: string | null;
  creator: string;
  clients: {
    first_name?: string;
    last_name?: string;
    phone_numbers: { phone_number: string }[];
  }[];
  front_attachments?: MessageAttachment[];
  front_attachment_urls?: string[];
  representing_attorney?: string | null;
  simplified_billing_code?: SimplifiedBillingCode | null;
  /** Task configuration for controlling task visibility during initial review */
  task_config?: {
    gate_tasks_during_initial_review?: boolean;
  };
}

export type {
  BaseMatter,
  Blob,
  BlobCreateRequest,
  BlobUpdateRequest,
  CollectionDetails,
  CollectionNode,
  Conversation,
  CreateTaskUpdatePayload,
  DetailedMatter,
  Engagement,
  MatterKeyConsideration,
  MatterKeyConsiderationSeverity,
  MatterSummary,
  MatterUpdate,
  PhoneCallAction,
  RelatedMatter,
  SharedMatter,
  ShareMatterLink,
  Task,
  TaskActionResult,
  TaskGroup,
  TaskSearchResults,
  TaskStatus,
  TaskType,
  TaskTypeAction,
  TaskUpdate,
  UpdateTaskExternalCMSStatusPayload,
  UpdateTaskPayload,
};
